import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import b from './BranchesSection.module.css';

/**
 * O3 (SO-28) — the "More" overflow menu on a branch card. Holds the rarely-used
 * and destructive actions (Delete) so they don't sit next to Edit as equals.
 *
 * Keyboard: the trigger is a real button (aria-haspopup / aria-expanded); opening
 * moves focus to the first item, ArrowUp/ArrowDown move between items, Escape or
 * Tab closes, and Escape returns focus to the trigger. Outside clicks close it.
 */
export interface BranchMoreMenuItem {
    key: string;
    label: string;
    onSelect: () => void;
    destructive?: boolean;
}

interface BranchMoreMenuProps {
    label: string;
    ariaLabel: string;
    items: BranchMoreMenuItem[];
}

const BranchMoreMenu: React.FC<BranchMoreMenuProps> = ({ label, ariaLabel, items }) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const menuId = useId();

    const close = useCallback((returnFocus: boolean) => {
        setOpen(false);
        if (returnFocus) triggerRef.current?.focus();
    }, []);

    useEffect(() => {
        if (!open) return;
        itemRefs.current[0]?.focus();
        const onDocMouseDown = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [open]);

    const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const count = items.length;
        const current = itemRefs.current.findIndex(el => el === document.activeElement);
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            close(true);
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            itemRefs.current[(current + 1 + count) % count]?.focus();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            itemRefs.current[(current - 1 + count) % count]?.focus();
        } else if (e.key === 'Home') {
            e.preventDefault();
            itemRefs.current[0]?.focus();
        } else if (e.key === 'End') {
            e.preventDefault();
            itemRefs.current[count - 1]?.focus();
        } else if (e.key === 'Tab') {
            close(false);
        }
    };

    return (
        <div className={b.moreWrap} ref={wrapRef}>
            <button
                ref={triggerRef}
                type="button"
                className={b.iconBtn}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                aria-label={ariaLabel}
                onClick={() => setOpen(o => !o)}
                onKeyDown={e => {
                    if (e.key === 'ArrowDown' && !open) {
                        e.preventDefault();
                        setOpen(true);
                    }
                }}
            >
                {label}
                <span className={b.moreCaret} aria-hidden="true">▾</span>
            </button>
            {open && (
                <div id={menuId} className={b.moreMenu} role="menu" aria-label={ariaLabel} onKeyDown={onMenuKeyDown}>
                    {items.map((item, i) => (
                        <button
                            key={item.key}
                            ref={el => { itemRefs.current[i] = el; }}
                            type="button"
                            role="menuitem"
                            tabIndex={-1}
                            className={`${b.moreItem} ${item.destructive ? b.moreItemDanger : ''}`}
                            onClick={() => {
                                close(false);
                                item.onSelect();
                            }}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default BranchMoreMenu;
